import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { useNavigate, useLocation } from 'react-router-dom';
import './tailwind.css';
import { generaPdfRisoluzione } from './utils/generaPdfRisoluzione.ts';
import {
  FaEuroSign,
  FaStickyNote,
  FaSave,
  FaTimes,
  FaSpinner,
  FaSearch,
  FaCalendarAlt,
  FaCheckCircle,
  FaExclamationTriangle
} from 'react-icons/fa';
import dayjs from 'dayjs';

export default function AnnullaPolizza() {
  const navigate = useNavigate();
  const location = useLocation();

  const [postConfirmModal, setPostConfirmModal] = useState({ show: false, policyId: null });

  // Stati per i dati del form
  const [polizzaCercata, setPolizzaCercata] = useState('');
  const [polizzaSelezionata, setPolizzaSelezionata] = useState(null);
  const [dataAnnullamento, setDataAnnullamento] = useState('');
  const [premioRestituito, setPremioRestituito] = useState(0);
  const [noteAnnullamento, setNoteAnnullamento] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [modal, setModal] = useState({ show: false, title: '', content: '', onConfirm: null });

  // Stato per i pacchetti garanzia (per il calcolo del valore base)
  const [pacchettiGaranzia, setPacchettiGaranzia] = useState([]);

  // Limiti date: oggi ±14 giorni
  const today = dayjs().startOf('day');
  const minDate = today.subtract(14, 'day');
  const maxDate = today.add(14, 'day');
  const minDateISO = minDate.format('YYYY-MM-DD');
  const maxDateISO = maxDate.format('YYYY-MM-DD');

  // Reset data annullamento quando cambia la polizza
 useEffect(() => {
  if (polizzaSelezionata && !dataAnnullamento) {
    // imposta la data solo se non è già stata scelta manualmente
    setDataAnnullamento(today.format('YYYY-MM-DD'));
  }
}, [polizzaSelezionata, today]);


  // Fetch dei pacchetti garanzia
  useEffect(() => {
    const fetchPacchettiGaranzia = async () => {
      const { data, error } = await supabase
        .from('pacchetti_garanzia')
        .select('id, valore_base, nome, tipologia, percentuale_netto');
      if (error) {
        console.error('Errore nel recupero pacchetti garanzia:', error);
      } else {
        setPacchettiGaranzia(data);
      }
    };
    fetchPacchettiGaranzia();
  }, []);

  // Carica polizza da query string ?id=
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const polizzaId = params.get('id');
    if (polizzaId) {
      setLoading(true);
      const fetchPolizzaById = async () => {
        const { data, error } = await supabase
          .from('polizze')
          .select(`
            *,
            pacchetti_garanzia ( id, nome, tipologia, valore_base, percentuale_netto )
          `)
          .eq('id', polizzaId)
          .single();

        if (error || !data) {
          setMessage({ type: 'error', text: 'Polizza non trovata.' });
        } else if (data.stato_polizza !== 'ATTIVA') {
          setMessage({ type: 'warning', text: `La polizza è in stato: ${data.stato_polizza}.` });
        } else {
          setPolizzaSelezionata(data);
        }
        setLoading(false);
      };
      fetchPolizzaById();
    }
  }, [location.search]);

  // Calcolo premio restituito
  const calcolaPremioRestituito = useCallback(() => {
    if (!polizzaSelezionata || !dataAnnullamento) return 0;

    const annullamentoDate = dayjs(dataAnnullamento).startOf('day');

    if (annullamentoDate.isBefore(minDate)) {
      setMessage({ type: 'warning', text: 'La data di annullamento non può essere più vecchia di 14 giorni.' });
      return 0;
    }
    if (annullamentoDate.isAfter(maxDate)) {
      setMessage({ type: 'warning', text: 'La data di annullamento non può essere oltre 14 giorni da oggi.' });
      return 0;
    }

    const fineDecorrenza = dayjs(polizzaSelezionata.fine_decorrenza).startOf('day');
    const monthsDiff = fineDecorrenza.diff(annullamentoDate, 'month');

    const pacchetto = pacchettiGaranzia.find(p => p.id === polizzaSelezionata.id_pacchetto_garanzia);
    if (!pacchetto) {
      console.error('Pacchetto garanzia non trovato.');
      return 0;
    }

    const valoreBaseNetto = pacchetto.valore_base * pacchetto.percentuale_netto;
    const valoreBaseNettoMensile = valoreBaseNetto / 12;

    return (monthsDiff > 0 ? monthsDiff * valoreBaseNettoMensile : 0).toFixed(2);
  }, [polizzaSelezionata, dataAnnullamento, pacchettiGaranzia, minDate, maxDate]);

  useEffect(() => {
    const importoCalcolato = calcolaPremioRestituito();
    setPremioRestituito(importoCalcolato);
  }, [dataAnnullamento, polizzaSelezionata, calcolaPremioRestituito]);

  // Ricerca polizza manuale
  // Ricerca polizza manuale (prima ATTIVA, poi fallback su non-attive)
const cercaPolizza = async () => {
  const term = polizzaCercata.trim();
  if (!term) {
    setMessage({ type: 'error', text: 'Inserisci un numero polizza o una targa.' });
    return;
  }

  setLoading(true);
  setPolizzaSelezionata(null);
  setMessage({ type: '', text: '' });

  const baseSelect = `
    *,
    pacchetti_garanzia ( tipologia, nome, valore_base, percentuale_netto )
  `;

  // 1) PRIORITÀ: cerca tra le ATTIVE
  const { data: active, error: errActive } = await supabase
    .from('polizze')
    .select(baseSelect)
    .or(`numero_polizza.eq.${term},targa.eq.${term}`)
    .eq('stato_polizza', 'ATTIVA')
    .order('updated_at', { ascending: false }) // o created_at / fine_decorrenza, in base al tuo schema
    .limit(1)
    .maybeSingle();

  if (errActive) {
    console.error('Errore ricerca ATTIVA:', errActive);
    setMessage({ type: 'error', text: 'Errore durante la ricerca. Riprova.' });
    setLoading(false);
    return;
  }

  if (active) {
    setPolizzaSelezionata(active);
    setLoading(false);
    return;
  }

  // 2) FALLBACK: cerca tra le NON attive (scaduta / annullata / eliminata / ecc.)
  const { data: fallback, error: errFallback } = await supabase
    .from('polizze')
    .select(baseSelect)
    .or(`numero_polizza.eq.${term},targa.eq.${term}`)
    .neq('stato_polizza', 'ATTIVA')
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (errFallback) {
    console.error('Errore ricerca NON attive:', errFallback);
    setMessage({ type: 'error', text: 'Errore durante la ricerca. Riprova.' });
    setLoading(false);
    return;
  }

  if (!fallback) {
    setMessage({ type: 'error', text: 'Nessuna polizza trovata per numero/targa.' });
    setLoading(false);
    return;
  }

  // trovato, ma non è annullabile
  setMessage({
    type: 'warning',
    text: `Nessuna polizza ATTIVA trovata. Ultima polizza trovata è in stato: ${fallback.stato_polizza}.`
  });
  setPolizzaSelezionata(fallback);
  setLoading(false);
};

  // Conferma annullamento
  const handleConfirmAnnullamento = async () => {
    setLoading(true);
    closeModal();
    setMessage({ type: '', text: '' });

    const { error } = await supabase
      .from('polizze')
      .update({
        stato_polizza: 'ANNULLATA',
        data_risoluzione: dataAnnullamento,
        premio_restituito: parseFloat(premioRestituito) || 0,
        note: noteAnnullamento,
        updated_at: new Date().toISOString()
      })
      .eq('id', polizzaSelezionata.id);

    setLoading(false);
    if (error) {
      console.error('Errore annullamento:', error);
      setMessage({ type: 'error', text: 'Errore durante l\'annullamento. Riprova.' });
    } else {
      setMessage({ type: 'success', text: 'Polizza annullata con successo!' });
  setPostConfirmModal({ show: true, policyId: polizzaSelezionata.id });
  resetForm();
    }
  };

  // Mostra conferma annullamento
  const showConfirmModal = () => {
    if (!polizzaSelezionata || !dataAnnullamento) {
      setMessage({ type: 'error', text: 'Devi prima selezionare una polizza e una data di annullamento.' });
      return;
    }

    const annullamentoDate = dayjs(dataAnnullamento).startOf('day');

    if (annullamentoDate.isBefore(minDate)) {
      setMessage({ type: 'error', text: 'La data di annullamento non può essere più vecchia di 14 giorni.' });
      return;
    }
    if (annullamentoDate.isAfter(maxDate)) {
      setMessage({ type: 'error', text: 'La data di annullamento non può essere oltre 14 giorni da oggi.' });
      return;
    }

    setModal({
      show: true,
      title: 'Conferma Annullamento',
      content: `Sei sicuro di voler annullare la polizza n. ${polizzaSelezionata.numero_polizza} a partire dal ${dayjs(dataAnnullamento).format('DD/MM/YYYY')}? L'importo da stornare sarà di €${premioRestituito}.`,
      onConfirm: handleConfirmAnnullamento
    });
  };

  const closeModal = () => setModal({ show: false, title: '', content: '', onConfirm: null });

  const renderModal = () => {
  if (!modal.show) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full text-center">
        <h3 className="text-xl font-bold mb-4 text-gray-800">{modal.title}</h3>
        <p className="text-gray-700 whitespace-pre-line mb-6">{modal.content}</p>
        <div className="flex flex-col space-y-3 w-full">
          <button
            type="button"
            className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            onClick={() => {
              modal.onConfirm();
              closeModal();
            }}
          >
            Conferma
          </button>
          <button
            type="button"
            className="w-full px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors"
            onClick={closeModal}
          >
            Annulla
          </button>
        </div>
      </div>
    </div>
  );
};

const renderPostConfirmModal = () => {
  if (!postConfirmModal.show) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full text-center">
        <h3 className="text-xl font-bold mb-4 text-gray-800">
          Operazione completata
        </h3>
        <p className="text-gray-700 whitespace-pre-line mb-6">
          La polizza è stata annullata correttamente.
        </p>
        <div className="flex flex-col space-y-3 w-full">
          <button
            type="button"
            className="w-full px-4 py-2 bg-miorosso text-white rounded-md hover:bg-miorossohover transition-colors"
            onClick={() => setPostConfirmModal({ show: false, policyId: null })}
          >
            OK
          </button>
          <button
            type="button"
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            onClick={() => handleDownloadRisoluzionePdf(postConfirmModal.policyId)}
          >
            Scarica PDF
          </button>
        </div>
      </div>
    </div>
  );
};


// ⭐ NUOVA FUNZIONE: Scarica PDF Risoluzione ⭐
const handleDownloadRisoluzionePdf = async (policyId) => {
  try {
    const blob = await generaPdfRisoluzione(policyId); // importa questa funzione dal tuo file utils
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `risoluzione_${policyId}.pdf`; // 🔹 Nome file chiaro
    a.click();
    URL.revokeObjectURL(url);
    setPostConfirmModal({ show: false, policyId: null }); // Chiudi modale dopo il download
  } catch (err) {
    setMessage({
      type: 'error',
      text: `Impossibile generare il PDF di risoluzione: ${err.message}.`
    });
  }
};

  const resetForm = () => {
    setPolizzaCercata('');
    setPolizzaSelezionata(null);
    setDataAnnullamento('');
    setNoteAnnullamento('');
    setPremioRestituito(0);
    setMessage({ type: '', text: '' });
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl p-8 transform transition-all duration-300 hover:scale-105">
        <h1 className="text-2xl md:text-3xl font-bold uppercase mb-4 text-center text-mioblu">
          Annullamento Adesioni
        </h1>
        <p className="text-center text-gray-600 mb-8">
          Cerca un'adesione esistente per annullarla e calcolare il premio da restituire.
        </p>

        {message.text && (
          <div
            className={`p-4 mb-6 rounded-lg text-sm font-medium flex items-center ${
              message.type === 'error'
                ? 'bg-red-100 text-red-700'
                : message.type === 'warning'
                ? 'bg-yellow-100 text-yellow-700'
                : 'bg-green-100 text-green-700'
            }`}
          >
            {message.type === 'error' ? (
              <FaExclamationTriangle className="mr-2" />
            ) : message.type === 'success' ? (
              <FaCheckCircle className="mr-2" />
            ) : null}
            {message.text}
          </div>
        )}

        {/* Ricerca */}
        <div className="relative mb-6">
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
            Cerca Adesione (N. Polizza o Targa)
          </label>
          <div className="flex">
            <input
              type="text"
              id="search"
              name="search"
              value={polizzaCercata}
              onChange={(e) => setPolizzaCercata(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-3 border"
              placeholder="Esempio: ABC-123-DEF o 123456789"
              disabled={loading}
            />
            <button
              onClick={cercaPolizza}
              className="ml-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
              disabled={loading}
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaSearch />}
              <span className="ml-2 hidden sm:inline-block">Cerca</span>
            </button>
          </div>
        </div>

        {/* Dettagli polizza */}
        {polizzaSelezionata && (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Dettagli Polizza</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700">Numero Polizza</label>
                <p className="mt-1 text-base text-gray-900 font-semibold">{polizzaSelezionata.numero_polizza}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700">Targa</label>
                <p className="mt-1 text-base text-gray-900 font-semibold">{polizzaSelezionata.targa}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700">Cliente</label>
                <p className="mt-1 text-base text-gray-900 font-semibold">
                  {polizzaSelezionata.cliente_privato
                    ? `${polizzaSelezionata.nome_cliente} ${polizzaSelezionata.cognome_cliente}`
                    : polizzaSelezionata.ragione_sociale}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700">Periodo di validità</label>
                <p className="mt-1 text-base text-gray-900 font-semibold">
                  {dayjs(polizzaSelezionata.inizio_decorrenza).format('DD/MM/YYYY')} -{' '}
                  {dayjs(polizzaSelezionata.fine_decorrenza).format('DD/MM/YYYY')}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700">Pacchetto Garanzia</label>
                <p className="mt-1 text-base text-gray-900 font-semibold">{polizzaSelezionata.pacchetti_garanzia.nome}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700">Valore Polizza</label>
                <p className="mt-1 text-base text-gray-900 font-semibold">€{polizzaSelezionata.valore_polizza_calcolato}</p>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label htmlFor="data_annullamento" className="block text-sm font-medium text-gray-700 flex items-center">
                  <FaCalendarAlt className="mr-2 text-gray-400" /> Data di Annullamento
                </label>
                <input
                  type="date"
                  id="data_annullamento"
                  name="data_annullamento"
                  value={dataAnnullamento}
                  onChange={(e) => {
                    setDataAnnullamento(e.target.value);
                    setMessage((m) =>
                      m.type === 'warning' || m.type === 'error' ? { type: '', text: '' } : m
                    );
                  }}
                  min={minDateISO}
                  max={maxDateISO}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-3 border"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 flex items-center">
                  <FaEuroSign className="mr-2 text-gray-400" /> Importo da Stornare
                </label>
                <input
                  type="text"
                  value={`€${premioRestituito}`}
                  readOnly
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm p-3 border cursor-not-allowed"
                />
              </div>

              <div>
                <label htmlFor="note_annullamento" className="block text-sm font-medium text-gray-700 flex items-center">
                  <FaStickyNote className="mr-2 text-gray-400" /> Note (Opzionale)
                </label>
                <textarea
                  id="note_annullamento"
                  name="note_annullamento"
                  rows="3"
                  value={noteAnnullamento}
                  onChange={(e) => setNoteAnnullamento(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-3 border"
                ></textarea>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6">
              <button
                type="button"
                className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white font-bold rounded-lg shadow-md hover:bg-red-700 transition-colors flex items-center justify-center"
                onClick={resetForm}
              >
                <FaTimes className="mr-2" /> Annulla
              </button>
              <button
                type="button"
                className="w-full sm:w-auto px-6 py-3 bg-green-600 text-white font-bold rounded-lg shadow-md hover:bg-green-700 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={showConfirmModal}
                disabled={loading || !dataAnnullamento}
              >
                {loading ? <FaSpinner className="animate-spin mr-2" /> : <FaSave className="mr-2" />} Conferma Annullamento
              </button>
            </div>
          </div>
        )}
      </div>
      {renderModal()}
{renderPostConfirmModal()}
    </div>
  );
}
